/**
 * Error classifiers for Wingman SSE errors.
 *
 * Detection priority:
 *   1. Structured Wingman problem `kind` (preferred — no string matching)
 *   2. Structured provider `code`
 *   3. String-pattern fallback (for providers that return unstructured errors)
 *
 * TRANSITIONAL: Once Wingman sends `{type: "error", code: "token_limit"}`
 * on all context-limit errors, the string patterns can be removed.
 */

export type AssistantFailure =
  | { kind: "trial_limit" }
  | { kind: "token_limit"; used?: number; max?: number }
  | { kind: "pdf_page_limit"; maxPages?: number }
  | { kind: "generation_failed" };

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const TOKEN_LIMIT_CODES = new Set(["token_limit", "context_length_exceeded"]);
const TOKEN_LIMIT_KINDS = new Set(["RequestTooLarge"]);
const PDF_LIMIT_CODES = new Set(["pdf_page_limit"]);
const TRIAL_LIMIT_CODE = "TRIAL_USAGE_LIMIT_REACHED";

const TOKEN_LIMIT_PATTERNS = [
  "context_length_exceeded",
  "maximum context length",
  "too many tokens",
  "token limit",
  "context window",
  "reduce the length",
  "model's maximum context",
  "input is too long",
  "prompt is too long",
  "request too large",
  "token count exceeds",
  "exceeds the maximum number of tokens",
] as const;

const PDF_LIMIT_PATTERN = "pages per pdf";

/** Extract human-readable strings from an error of any shape. */
function getErrorText(error: unknown): string {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;

  const obj = error as Record<string, unknown>;
  const parts: string[] = [];
  if (typeof obj.message === "string") parts.push(obj.message);
  if (typeof obj.errorText === "string") parts.push(obj.errorText);
  if (typeof obj.code === "string") parts.push(obj.code);
  if (typeof obj.detail === "string") parts.push(obj.detail);
  if (obj.error) parts.push(getErrorText(obj.error));
  return parts.join(" ");
}

function getErrorCode(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null) {
    const code = (error as Record<string, unknown>).code;
    if (typeof code === "string") return code;
  }
  return undefined;
}

function getErrorKind(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null) {
    const kind = (error as Record<string, unknown>).kind;
    if (typeof kind === "string") return kind;
  }
  return undefined;
}

function textContains(text: string, patterns: readonly string[]): boolean {
  const lower = text.toLowerCase();
  return patterns.some((pattern) => lower.includes(pattern));
}

export function isTokenLimitError(error: unknown): boolean {
  const kind = getErrorKind(error);
  if (kind && TOKEN_LIMIT_KINDS.has(kind)) return true;

  const code = getErrorCode(error);
  if (code && TOKEN_LIMIT_CODES.has(code)) return true;

  const text = getErrorText(error);
  if (!text) return false;
  if (text.toLowerCase().includes(PDF_LIMIT_PATTERN)) return false;
  return textContains(text, TOKEN_LIMIT_PATTERNS);
}

export function isPdfPageLimitError(error: unknown): boolean {
  const code = getErrorCode(error);
  if (code && PDF_LIMIT_CODES.has(code)) return true;

  return getErrorText(error).toLowerCase().includes(PDF_LIMIT_PATTERN);
}

export function parsePdfMaxPages(error: unknown): number | undefined {
  const match = /maximum of (\d+) pages/i.exec(getErrorText(error));
  return match ? Number.parseInt(match[1], 10) : undefined;
}

/** Parse `N tokens > M maximum` from Gemini/wingman-v2 prompt-too-long errors. */
export function parseTokenLimitCounts(error: unknown): { used: number; max: number } | undefined {
  const match = /(\d+)\s*tokens\s*>\s*(\d+)\s*maximum/i.exec(getErrorText(error));
  if (!match) return undefined;
  return {
    used: Number.parseInt(match[1], 10),
    max: Number.parseInt(match[2], 10),
  };
}

/** The org's trial conversations are used up (wingman-v2's stable 403 code). */
export function isTrialLimitFailure(error: unknown): boolean {
  return getErrorCode(error) === TRIAL_LIMIT_CODE;
}

/** Map any failed turn onto the outcomes web shows. */
export function classifyAssistantFailure(error: unknown): AssistantFailure {
  if (isTrialLimitFailure(error)) return { kind: "trial_limit" };
  if (isPdfPageLimitError(error)) {
    return { kind: "pdf_page_limit", maxPages: parsePdfMaxPages(error) };
  }
  if (isTokenLimitError(error)) {
    const counts = parseTokenLimitCounts(error);
    return counts ? { kind: "token_limit", used: counts.used, max: counts.max } : { kind: "token_limit" };
  }
  return { kind: "generation_failed" };
}

/**
 * Terminal UI-message chunks. `data-generationFailed` is always the generic
 * apology — web does not surface the raw reason code.
 */
export function generationStreamFailure(value: unknown): unknown | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  const type = typeof record.type === "string" ? record.type : "";
  if (type === "error") return record.error ?? record.errorText ?? record;
  if (type === "data-generationError") return record.data ?? record;
  if (type === "data-generationFailed" || type === "generation_failed") return { kind: "generation_failed" };
  return null;
}

export function assistantFailureText(failure: AssistantFailure, t: TranslateFn): string {
  if (failure.kind === "trial_limit") return t("trialConversationLimitReached");
  if (failure.kind === "pdf_page_limit") {
    return failure.maxPages
      ? t("pdfPageLimitExceeded", { maxPages: failure.maxPages })
      : t("pdfPageLimitExceededGeneric");
  }
  if (failure.kind === "token_limit") {
    return failure.used != null && failure.max != null
      ? t("tokenLimitExceededWithCounts", { used: failure.used, max: failure.max })
      : t("tokenLimitReached");
  }
  return t("sorryErrorSendingMessage");
}

export function assistantFailureAlert(failure: AssistantFailure, t: TranslateFn): string {
  if (failure.kind === "trial_limit") return t("trialConversationLimitReachedAlert");
  if (failure.kind === "token_limit" && (failure.used == null || failure.max == null)) {
    return t("tokenLimitReachedAlert");
  }
  if (failure.kind === "generation_failed") return t("sendingMessageFailed");
  return assistantFailureText(failure, t);
}
